import { Injectable } from '@angular/core';
import {
  AuditPermissionRole,
  PermissionRole,
  PermissionRoleNumber,
} from '../../models/enum/permission-role-enum';
import { ISearchDw } from '../../models/interface/aunnual-audit/aunnual-audit';
import { IAccessRightsSubs } from '../../models/interface/login/i-login';

@Injectable({
  providedIn: 'root',
})
export class RolePermissionService {
  getCurrentUserRole() {
    return PermissionRole.Staff;
  }

  getPermissionRoleNameEnum(roleNumber: PermissionRoleNumber): PermissionRole {
    const roleMap: { [key in PermissionRoleNumber]?: PermissionRole } = {
      [PermissionRoleNumber.InformationTechnologyCenter]:
        PermissionRole.InformationTechnologyCenter,
      [PermissionRoleNumber.AssetManagementDirector]:
        PermissionRole.AssetManagementDirector,
      [PermissionRoleNumber.AssetManagementTeam]:
        PermissionRole.AssetManagementTeam,
      [PermissionRoleNumber.DepartmentAdmin]: PermissionRole.DepartmentAdmin,
      [PermissionRoleNumber.DivisionDirector]: PermissionRole.DivisionDirector,
      [PermissionRoleNumber.GroupDirector]: PermissionRole.GroupDirector,
      [PermissionRoleNumber.MaterialManager]: PermissionRole.MaterialManager,
      [PermissionRoleNumber.MaterialOfficer]: PermissionRole.MaterialOfficer,
      [PermissionRoleNumber.Staff]: PermissionRole.Staff,
      [PermissionRoleNumber.InternalAuditGroup]:
        PermissionRole.InternalAuditGroup,
      [PermissionRoleNumber.TreasuryDivision]: PermissionRole.TreasuryDivision,
      [PermissionRoleNumber.PlanningPerformanceDivision]:
        PermissionRole.PlanningPerformanceDivision,
      [PermissionRoleNumber.InventoryCheckingCommittee]:
        PermissionRole.InventoryCheckingCommittee,
      [PermissionRoleNumber.CarAuditor]: PermissionRole.CarAuditor,
      [PermissionRoleNumber.Driver]: PermissionRole.Driver,
    };

    return roleMap[roleNumber] ?? PermissionRole.Staff;
  }

  isDirectorApproveRole(roleName: PermissionRole): boolean {
    return roleName === PermissionRole.DivisionDirector;
  }

  isAuditCommitteeRole(roleId: PermissionRoleNumber): boolean {
    return roleId === PermissionRoleNumber.InventoryCheckingCommittee;
  }

  isMaterialAuditCommitteeRole(roleSubs: IAccessRightsSubs[]): boolean {
    return roleSubs.some(
      (role) =>
        role.access_rights_id ===
        PermissionRoleNumber.InventoryCheckingCommittee
    );
  }

  isMaterialManagerRole(roleId: PermissionRoleNumber): boolean {
    return roleId === PermissionRoleNumber.MaterialManager;
  }

  getAuditCommittee(): ISearchDw[] {
    const AuditCommitteeRoles: ISearchDw[] = [
      {
        id: 1,
        name: AuditPermissionRole.InventoryAuditor,
      },
      {
        id: 2,
        name: AuditPermissionRole.CountingChairman,
      },
      {
        id: 3,
        name: AuditPermissionRole.AuditCommittee,
      },
      {
        id: 4,
        name: AuditPermissionRole.Chairperson,
      },
      {
        id: 5,
        name: AuditPermissionRole.CommitteeMember,
      },
    ];
    return AuditCommitteeRoles;
  }

  getMaterialAuditCommittee(): ISearchDw[] {
    const AuditCommitteeRoles: ISearchDw[] = [
      {
        id: PermissionRoleNumber.InventoryCheckingCommittee,
        name: AuditPermissionRole.InventoryAuditor,
      },
      {
        id: PermissionRoleNumber.InventoryCheckingCommittee,
        name: AuditPermissionRole.CountingChairman,
      },
      {
        id: PermissionRoleNumber.InventoryCheckingCommittee,
        name: AuditPermissionRole.AuditCommittee,
      },
    ];
    return AuditCommitteeRoles;
  }

  initializeUserRoles() {
    const userRoles: Record<
      string,
      { isRole: boolean; roleName: string; access_rights_id: number }
    > = {};
    Object.keys(PermissionRoleNumber).forEach((key) => {
      const roleKey = key as keyof typeof PermissionRoleNumber;
      const accessRightId = PermissionRoleNumber[roleKey];
      const roleName = PermissionRole[roleKey];
      const userRoleKey = `is${roleKey}`;

      if (roleName) {
        userRoles[userRoleKey] = {
          isRole: false,
          roleName: roleName,
          access_rights_id: accessRightId,
        };
      }
    });
    return userRoles;
  }

  setUserRole(
    accessRights: { access_rights_id: number; access_rights_name: string }[]
  ) {
    let updateAccessRightsRole = this.initializeUserRoles();

    accessRights.forEach(({ access_rights_id, access_rights_name }) => {
      const roleKey = Object.keys(PermissionRoleNumber).find(
        (key) =>
          PermissionRoleNumber[key as keyof typeof PermissionRoleNumber] ===
          access_rights_id
      );

      if (roleKey) {
        const role = `is${roleKey}`;
        updateAccessRightsRole[role].isRole = true;
        updateAccessRightsRole[role].roleName = access_rights_name;
        updateAccessRightsRole[role].access_rights_id = access_rights_id;
      } else {
        const newRoleKey = `isAccessRightsId${access_rights_id}`;
        updateAccessRightsRole[newRoleKey] = {
          isRole: true,
          roleName: access_rights_name,
          access_rights_id,
        };
      }
    });

    return updateAccessRightsRole;
  }
}
